import './InfoTooltip.scss';
import cn from 'classnames';
import { ComponentProps, CSSProperties, ReactNode, useState } from 'react';
import { OverlayTrigger, Popover } from 'react-bootstrap';
import { MdInfoOutline } from 'react-icons/md';
import { useDebouncedCallback } from '@src/utils/hooks/useDebouncedCallback';
import { Appearance, Placement, PlacementType } from '../designConstants';
import { IconButton } from '../IconButton';
import { InteractiveDiv } from '../InteractiveEl';

interface InfoTooltipProps extends Omit<ComponentProps<typeof IconButton>, 'appearance'> {
  placement?: PlacementType;
  rightLabel?: string | ReactNode;
  iconColor?: CSSProperties['color'];
  textColor?: CSSProperties['color'];
  /** Extra class on the popover, for callers that need to scope its surface styling. */
  popoverClassName?: string;
  /**
   * Set when this tooltip sits inline in running text (e.g. at the end of a wrapping paragraph)
   * rather than as a flex/grid item next to a label -- only the former is affected by
   * `vertical-align`, so only there does the sub-pixel baseline correction apply. Leaving this
   * false (the common case: a flex row pairing a label with this icon) avoids nudging the icon
   * off-center against text that isn't in inline flow to begin with.
   */
  inline?: boolean;
  /**
   * For popovers with links or other controls: opens on click or a short hover, stays open on
   * mouse leave, and closes on an outside click or Escape.
   */
  interactive?: boolean;
}

const INTERACTIVE_HOVER_DELAY_MS = 300;

export const InfoTooltip = ({
  children,
  placement = Placement.AUTO,
  rightLabel,
  iconColor,
  textColor,
  className,
  popoverClassName,
  inline = false,
  interactive = false,
  ...iconButtonPassthroughProps
}: InfoTooltipProps) => {
  const [show, setShow] = useState(false);
  const [scheduleShow, cancelShow] = useDebouncedCallback(
    () => setShow(true),
    INTERACTIVE_HOVER_DELAY_MS
  );

  const overlayTriggerProps = interactive
    ? { trigger: [], show, onToggle: setShow, rootClose: true }
    : { trigger: ['hover' as const, 'focus' as const] };

  const interactiveIconProps = interactive
    ? {
        onMouseEnter: scheduleShow,
        onMouseLeave: cancelShow,
        onFocus: () => setShow(true),
        onClick: () => {
          cancelShow();
          setShow(true);
        },
        'aria-expanded': show
      }
    : {};

  return (
    <InteractiveDiv
      className={cn('tooltip-container', { inline })}
      onClick={e => e.stopPropagation()}
    >
      <OverlayTrigger
        {...overlayTriggerProps}
        placement={placement}
        overlay={
          <Popover className={cn('info-tooltip-popover', popoverClassName, { interactive })}>
            <Popover.Body style={{ color: textColor }}>{children}</Popover.Body>
          </Popover>
        }
      >
        <IconButton
          {...iconButtonPassthroughProps}
          {...interactiveIconProps}
          appearance={Appearance.EMPTY}
          className={cn('info-icon', className)}
        >
          <MdInfoOutline color={iconColor} />
        </IconButton>
      </OverlayTrigger>
      {rightLabel && (
        <div className='right-label' style={{ color: textColor }}>
          {rightLabel}
        </div>
      )}
    </InteractiveDiv>
  );
};
